import React, { useCallback, useMemo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArchive,
  faBallotCheck,
  faBug,
  faCheckCircle,
  faClockRotateLeft,
  faCopy,
  faEdit,
  faEye,
  faLightbulbGear,
  faMessageLines,
  faPlugCirclePlus,
  faPlus,
  faSearch,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import { Flow, FlowVersion } from "src/api/flowTypes";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import {
  etag_including_comments,
  useLoadChangeHistory,
} from "src/changeHistory/queries";
import {
  useLatestSeenReviewEtag,
  useLatestSeenVersionEtag,
} from "src/changeHistory/useLatestSeenVersionEtag";
import { FlowVersionStatus } from "src/clients/flow-api";
import { IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { FlowVersionControlModals } from "src/flow/FlowVersionControlModals";
import { getExportToJSONOption } from "src/flow/getExportToJSON";
import { usePrefetchAgentDepsForFlowVersion } from "src/flow/useAgentDependencies";
import {
  useArchiveAction,
  useFlowVersionModals,
} from "src/flow/useFlowVersionModals";
import { usePublishFlowVersionCallback } from "src/flow/usePublishFlowVersionCallback";
import { ContinueEditingVersionButtonWrapper } from "src/flowContainer/ContinueEditingVersionButton/ContinueEditingVersionButtonWrapper";
import { DebugManifestModal } from "src/flowContainer/DebugManifestModal";
import {
  useCompileManifest,
  useUploadManifest,
} from "src/flowContainer/hooks/useManifestBuilder";
import { RightPanes, useRightPane } from "src/flowContainer/hooks/useRightPane";
import { PromoteFlowVersionModal } from "src/flowContainer/ManifestPromotion/PromoteFlowVersionModal";
import { RunFlowButtonWrapper } from "src/flowContainer/RunFlowButton/RunFlowButtonWrapper";
import {
  canAddReview,
  canRequestReview,
  publishingBlockedReason,
} from "src/flowContainer/versionActionConditions";
import { useFlowVersionReview } from "src/flowReview/api/queries";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useHasSimulation } from "src/hooks/useHasSimulation";
import { JobRun } from "src/jobs/types";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getUrlToAuthoringPage } from "src/router/urls";
import {
  useCreateFailedSimulationDatasetJob,
  useCreateSimulationDownloadDatasetJob,
  useCreateSimulationLinkDatasetJob,
  useMarkSimulationAsDeleted,
} from "src/simulations/api/mutations";
import { CreateDatasetFromFailedRowsModal } from "src/simulations/CreateDatasetFromFailedRowsModal";
import { SimulationOptionsDropdown } from "src/simulations/SimulationOptionsDropdown";
import { isSimulationInProgress } from "src/simulations/simulationUtils";
import { useSimulationJob } from "src/simulations/useSimulationJob";
import { useSimulationRun } from "src/simulations/useSimulationRun";
import { useCurrentUserId } from "src/store/AuthStore";
import { modifierKey } from "src/utils/platformModifierKey";

type ExcludeFalse<T> = Exclude<T, false>;
type HeaderIconProps = {
  icon: IconProp;
  onClick: () => void;
  showNotification?: boolean;
  dataLoc?: string;
  isActive: boolean;
  tooltipTitle: React.ReactNode;
  tooltipBody?: string;
};

const HeaderIcon: React.FC<HeaderIconProps> = ({
  icon,
  onClick,
  showNotification,
  dataLoc,
  isActive,
  tooltipTitle,
  tooltipBody,
}) => {
  return (
    <div className="relative items-center">
      <Tooltip
        body={tooltipBody}
        placement="bottom-end"
        title={tooltipTitle}
        asChild
      >
        <IconButton
          isActive={isActive}
          dataLoc={dataLoc}
          icon={icon}
          size="xs"
          onClick={onClick}
        />
      </Tooltip>
      {showNotification && (
        <div className="absolute top-[3px] right-0.5 h-1.5 w-1.5 rounded-full border border-1 border-white bg-indigo-600" />
      )}
    </div>
  );
};

const VersionOptionsDropdown: React.FC<{
  flow: Flow;
  flowVersion: FlowVersion;
}> = ({ flow, flowVersion }) => {
  const { workspace } = useAuthoringContext();
  const { setRightPane } = useRightPane();
  const {
    currentModal,
    buildOpenModalAction,
    onClose,
    onAfterLeave,
    selectedVersion,
    unpublishedTemplates,
    onOpenManageTrafficModal,
  } = useFlowVersionModals();
  const { getArchiveAction } = useArchiveAction(buildOpenModalAction);
  const { flowVersions: flowVersionsCapabilities } = useCapabilities();
  const prefetchAgentDeps = usePrefetchAgentDepsForFlowVersion();
  const { data: review } = useFlowVersionReview(flowVersion.id);
  const handlePublish = usePublishFlowVersionCallback({
    flowId: flow.id,
    buildOpenModalAction,
  });
  const exportToJSONOption = getExportToJSONOption(flow.name, flowVersion);
  const canEditFlowVersion = useCanAuthoringEditFlowVersion();
  const { mutate: uploadManifest } = useUploadManifest();
  const { mutateAsync: compileManifest } = useCompileManifest();
  const {
    isOpen: isPromoteModalOpen,
    openModal: openPromoteModal,
    closeModal: closePromoteModal,
    afterLeave: promoteModalAfterLeave,
  } = useModal();
  const {
    isOpen: isDebugManifestModalOpen,
    openModal: openDebugManifestModal,
    closeModal: closeDebugManifestModal,
    afterLeave: debugManifestModalAfterLeave,
    data: debugManifestData,
  } = useModal<{
    manifest: Record<string, unknown>;
    jinjaFile: { content: string; name: string } | null;
    testCaseRows: Record<string, unknown>[] | null;
    sampleResponseRows: Record<string, unknown>[] | null;
    flowUrl: string;
  }>();

  const handleDebugManifest = useCallback(async () => {
    try {
      const result = await compileManifest({
        flowVersionId: flowVersion.id,
      });
      if (result) {
        openDebugManifestModal({
          manifest: result.manifest,
          jinjaFile: result.jinja_file,
          testCaseRows: result.test_case_rows,
          sampleResponseRows: result.sample_response_rows,
          flowUrl: result.flow_url,
        });
      }
    } catch {
      // Error is already handled by the mutation's onError callback
    }
  }, [compileManifest, flowVersion.id, openDebugManifestModal]);

  const optionElements: OptionsDropdownElement[] = useMemo(() => {
    const { canCreate, canDeleteDraft, canArchive } = flowVersionsCapabilities;
    const isDraft = flowVersion.status === FlowVersionStatus.DRAFT;
    const isPublished = flowVersion.status === FlowVersionStatus.PUBLISHED;
    const isArchived = flowVersion.status === FlowVersionStatus.ARCHIVED;
    const canPublish = flowVersionsCapabilities.canPublish(flow);
    const manifestBuilderEnabled = isFeatureFlagEnabled(
      FEATURE_FLAGS.manifestBuilder,
    );

    const secondGroup = [
      canRequestReview(flow, flowVersion.status, review) && {
        key: "Request review",
        icon: faEye,
        action: buildOpenModalAction("review_version", flowVersion),
      },
      canAddReview(review, flowVersion.status) && {
        key: "Add review",
        icon: faPlus,
        action: () =>
          setRightPane(RightPanes.ReviewProcess, {
            focusAddReview: true,
          }),
      },
      canPublish &&
        isDraft && {
          key: "Publish",
          icon: faCheckCircle,
          action: () => handlePublish(flowVersion),
          disabled: publishingBlockedReason(flow, review),
        },
      canPublish &&
        isArchived && {
          key: "Republish",
          icon: faCheckCircle,
          action: buildOpenModalAction("republish_version", flowVersion),
        },
      manifestBuilderEnabled && {
        key: "Promote",
        icon: faPlugCirclePlus,
        action: () => openPromoteModal(),
      },
      manifestBuilderEnabled && {
        key: "Test Manifest",
        icon: faLightbulbGear,
        action: () => uploadManifest({ flowVersionId: flowVersion.id }),
      },
      manifestBuilderEnabled && {
        key: "Debug Manifest",
        icon: faBug,
        action: handleDebugManifest,
      },
    ].filter((x): x is ExcludeFalse<typeof x> => Boolean(x));

    const thirdGroup = [
      canArchive &&
        isPublished && {
          key: "Archive",
          icon: faArchive,
          action: getArchiveAction(flowVersion),
        },
      canDeleteDraft &&
        isDraft && {
          key: "Delete",
          icon: faTrashCan,
          action: buildOpenModalAction("delete_version", flowVersion),
        },
    ].filter((x): x is ExcludeFalse<typeof x> => Boolean(x));

    return [
      canCreate && {
        key: "Duplicate",
        icon: faCopy,
        action: buildOpenModalAction("add_version", flowVersion),
      },
      exportToJSONOption,
      canEditFlowVersion && {
        key: "Edit version details",
        icon: faEdit,
        action: buildOpenModalAction("edit_version", flowVersion),
      },
      (canEditFlowVersion || canCreate) && MENU_DIVIDER,
      ...secondGroup,
      secondGroup.length > 0 && thirdGroup.length > 0 && MENU_DIVIDER,
      ...thirdGroup,
    ].filter((x): x is ExcludeFalse<typeof x> => Boolean(x));
  }, [
    flowVersionsCapabilities,
    flowVersion,
    flow,
    review,
    handlePublish,
    buildOpenModalAction,
    setRightPane,
    exportToJSONOption,
    canEditFlowVersion,
    openPromoteModal,
    uploadManifest,
    handleDebugManifest,
  ]);

  return (
    <>
      <EllipsisOptionsDropdown
        dataLoc="flow-header-version-options"
        elements={optionElements}
        onOpenChanged={(isOpen) => {
          if (isOpen) prefetchAgentDeps(flow.slug, flowVersion.id);
        }}
      />
      <FlowVersionControlModals
        afterLeave={onAfterLeave}
        flow={flow}
        modal={currentModal}
        unpublishedTemplates={unpublishedTemplates}
        version={flow.versions.find(
          (version) => version.id === selectedVersion?.id,
        )}
        onClose={onClose}
        openManageTrafficModal={onOpenManageTrafficModal}
      />
      <PromoteFlowVersionModal
        afterLeave={promoteModalAfterLeave}
        flowVersionId={flowVersion.id}
        key={flowVersion.id}
        open={isPromoteModalOpen}
        onClose={closePromoteModal}
      />
      {debugManifestData && (
        <DebugManifestModal
          afterLeave={debugManifestModalAfterLeave}
          baseUrl={workspace.base_url ?? ""}
          flowUrl={debugManifestData.flowUrl}
          initialManifest={debugManifestData.manifest}
          jinjaFile={debugManifestData.jinjaFile}
          open={isDebugManifestModalOpen}
          sampleResponseRows={debugManifestData.sampleResponseRows}
          testCaseRows={debugManifestData.testCaseRows}
          onClose={closeDebugManifestModal}
        />
      )}
    </>
  );
};

const ConditionalSimulationOptionsDropdown: React.FC<{
  simulationJobRun: JobRun | undefined;
  handleDeleteSimulation: () => void;
  onCreateFailedDataset: () => void;
  handleDownloadCsv: () => void;
  handleDownloadNdjson: () => void;
  handleCopyCsvUrl: () => void;
  handleCopyNdjsonUrl: () => void;
}> = ({
  simulationJobRun,
  handleDeleteSimulation,
  onCreateFailedDataset,
  handleDownloadCsv,
  handleDownloadNdjson,
  handleCopyCsvUrl,
  handleCopyNdjsonUrl,
}) => {
  if (!simulationJobRun || isSimulationInProgress(simulationJobRun)) {
    return null;
  }

  return (
    <SimulationOptionsDropdown
      includeStopOption={false}
      simulation={simulationJobRun}
      onCopyCsvUrl={handleCopyCsvUrl}
      onCopyNdjsonUrl={handleCopyNdjsonUrl}
      onCreateFailedDataset={onCreateFailedDataset}
      onDelete={handleDeleteSimulation}
      onDownloadCsv={handleDownloadCsv}
      onDownloadNdjson={handleDownloadNdjson}
    />
  );
};

export const FlowHeaderButtons: React.FC = observer(() => {
  const { rightPane, setRightPane } = useRightPane();
  const { flowVersions } = useCapabilities();
  const { showOmnibox } = useOmniboxActions();

  const {
    workspace,
    orgId,
    flow,
    version,
    simulationId = "",
  } = useAuthoringContext();
  const hasSimulation = useHasSimulation();
  const changeHistory = useLoadChangeHistory(
    version.id,
    /* To ensure we query include comments against current flow_version,
we issue a before_etag with higher extended etag */
    etag_including_comments(version.etag),
    {
      includeLogic: true,
      includeReviewComments: true,
    },
  );
  const userId = useCurrentUserId();
  const { data: flowVersionReview } = useFlowVersionReview(version.id);

  const [latestSeenEtag] = useLatestSeenVersionEtag({
    userId: userId!,
    versionId: version.id,
  });

  const [latestSeenReviewEtag] = useLatestSeenReviewEtag({
    userId: userId!,
    versionId: version.id,
  });

  const navigate = useNavigate();
  const { simulationJob } = useSimulationJob(flow.id, workspace.base_url ?? "");
  const { data: simulationJobRun } = useSimulationRun(
    flow.id,
    version.id,
    simulationId,
  );
  const {
    isOpen: isCreateFailedDatasetModalOpen,
    closeModal: closeCreateFailedDatasetModal,
    openModal: openCreateFailedDatasetModal,
    data: createFailedDatasetModalData,
  } = useModal<{ simulationRunId: string }>();

  const { mutate: markSimulationAsDeletedMutation } =
    useMarkSimulationAsDeleted(workspace.base_url!, simulationJob?.id || "");
  const handleDeleteSimulation = () => {
    markSimulationAsDeletedMutation(simulationJobRun?.id || "");
    navigate(getUrlToAuthoringPage(orgId, workspace.id, flow.id, version.id));
  };

  const {
    mutateAsync: convertFailedToTestDataset,
    isPending: isDatasetCreationPending,
  } = useCreateFailedSimulationDatasetJob(workspace.base_url!, flow.id);
  const handleConvertFailedToTestDataset = (
    simulationJobRunId: string,
    name: string,
  ) => {
    return convertFailedToTestDataset({ simulationJobRunId, name });
  };
  const { mutate: createDownloadJob } = useCreateSimulationDownloadDatasetJob(
    workspace.base_url!,
    flow.id,
  );
  const { mutateAsync: createDownloadJobForCopy } =
    useCreateSimulationLinkDatasetJob(workspace.base_url!, flow.id);
  const handleDownloadCsv = () => {
    createDownloadJob({
      simulationJobRunId: simulationId,
      format: "csv",
    });
  };
  const handleDownloadNdjson = () => {
    createDownloadJob({
      simulationJobRunId: simulationId,
      format: "ndjson",
    });
  };
  const handleCopyCsv = () => {
    createDownloadJobForCopy({
      simulationJobRunId: simulationId,
      format: "csv",
    });
  };
  const handleCopyNdjson = () => {
    createDownloadJobForCopy({
      simulationJobRunId: simulationId,
      format: "ndjson",
    });
  };

  const unseenRevisions: boolean =
    changeHistory.data?.pages
      ?.flatMap((page) => page)
      .some(
        (changeLog) =>
          changeLog.diff &&
          changeLog.etag > latestSeenEtag &&
          changeLog.authored_by !== userId,
      ) || false;

  const unseenReview: boolean =
    changeHistory.data?.pages
      ?.flatMap((page) => page)
      .some((changeLog) => {
        // change logic or comments should be considered a review change
        return (
          changeLog.etag > latestSeenReviewEtag &&
          changeLog.authored_by !== userId
        );
      }) || false;

  const handleTogglePane = (pane: RightPanes) => {
    setRightPane(rightPane === pane ? null : pane);
  };

  return (
    <div className="flex flex-row items-center">
      <div className="mr-2 flex flex-row items-center gap-x-2">
        <div className="px-1">
          <HeaderIcon
            dataLoc="flow-header-omnibox"
            icon={faSearch}
            isActive={false}
            tooltipTitle={
              <>
                <span className="mr-2">Search</span>
                <Pill size="sm" variant="dark-gray">
                  <Pill.Text fontType="text">{modifierKey} K</Pill.Text>
                </Pill>
              </>
            }
            onClick={showOmnibox}
          />
        </div>
        <div className="px-1">
          <HeaderIcon
            dataLoc="flow-header-comments-button"
            icon={faMessageLines}
            isActive={rightPane === RightPanes.Comments}
            tooltipBody="View, reply and collaborate with your team on comments."
            tooltipTitle="Comments"
            onClick={() => handleTogglePane(RightPanes.Comments)}
          />
        </div>
        <div className="pr-1">
          <HeaderIcon
            dataLoc="change-history-open-history-icon"
            icon={faClockRotateLeft}
            isActive={rightPane === RightPanes.ChangeHistory}
            showNotification={unseenRevisions}
            tooltipBody="Inspect changes in chronological order"
            tooltipTitle="History"
            onClick={() => handleTogglePane(RightPanes.ChangeHistory)}
          />
        </div>
        {flowVersionReview && (
          <div className="pr-1">
            <HeaderIcon
              dataLoc="review-process-open-pane-icon"
              icon={faBallotCheck}
              isActive={rightPane === RightPanes.ReviewProcess}
              showNotification={unseenReview}
              tooltipBody="View review request, leave comments, and collaborate on the review process"
              tooltipTitle="Review process"
              onClick={() => handleTogglePane(RightPanes.ReviewProcess)}
            />
          </div>
        )}
      </div>
      {flowVersions.canEdit && !simulationId && !hasSimulation && (
        <div className="pr-4" data-loc="run-flow-button-wrapper">
          <RunFlowButtonWrapper flow={flow} version={version} />
        </div>
      )}
      {!simulationId && hasSimulation && (
        <div className="pr-4" data-loc="continue-editing-flow-button-wrapper">
          <ContinueEditingVersionButtonWrapper version={version} />
        </div>
      )}
      {simulationId ? (
        <>
          <ConditionalSimulationOptionsDropdown
            handleCopyCsvUrl={handleCopyCsv}
            handleCopyNdjsonUrl={handleCopyNdjson}
            handleDeleteSimulation={handleDeleteSimulation}
            handleDownloadCsv={handleDownloadCsv}
            handleDownloadNdjson={handleDownloadNdjson}
            simulationJobRun={simulationJobRun}
            onCreateFailedDataset={() =>
              openCreateFailedDatasetModal({
                simulationRunId: simulationJobRun?.id || "",
              })
            }
          />
          <CreateDatasetFromFailedRowsModal
            closeModal={closeCreateFailedDatasetModal}
            isDatasetCreationPending={isDatasetCreationPending}
            isOpen={isCreateFailedDatasetModalOpen}
            simulationJobRunId={createFailedDatasetModalData?.simulationRunId}
            onCreateFailedDataset={handleConvertFailedToTestDataset}
          />
        </>
      ) : (
        <VersionOptionsDropdown flow={flow} flowVersion={version} />
      )}
    </div>
  );
});
